import React, { useState, useCallback, useMemo } from 'react';
import { Fixture, GameState, Player, DisplayFixture } from '../../domain/entities';
import { MatchDetailsModal } from './MatchDetailsModal';
import { soundService } from '../../services/SoundService';
import { View } from '../../App';
import { CUP_NAMES, WOMEN_CUP_NAMES } from '../../domain/constants';
import { FixturesHelper } from '../../services/FixturesHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { getLeagueColor } from '../../utils/iconUtils';
import { TeamBadge } from './TeamBadge';

interface TeamFixturesProps {
    teamName: string;
    leagueName: string;
    gameState: GameState;
    isPlayerTeam: boolean;
    pushView: (view: View) => void;
    isAiPunditEnabled: boolean;
    showRatingsOnMatchReport: boolean;
    onCacheAiMatchReport: (fixture: DisplayFixture, report: { title: string; message: string; }) => void;
}

const getResultClass = (fixture: Fixture, subjectTeamName: string): string => {
    if (!fixture.result) return '';
    const { home, away, result } = fixture;
    const { homeScore, awayScore } = result;

    if (homeScore === awayScore) return 'result-draw';
    if (home === subjectTeamName) {
        return homeScore > awayScore ? 'result-win' : 'result-loss';
    }
    if (away === subjectTeamName) {
        return awayScore > homeScore ? 'result-win' : 'result-loss';
    }
    return '';
};

const getCompetitionEmoji = (competition: string, gameState: GameState) => {
    const cupNames = gameState.gameType === 'womens' ? WOMEN_CUP_NAMES : CUP_NAMES;
    if (competition === cupNames[0]) {
        return <FontAwesomeIcon icon={faTrophy} style={{ color: '#FFD700', marginRight: '4px' }} />; // Pixel Cup - gold
    }
    if (competition === cupNames[1]) {
        return <FontAwesomeIcon icon={faTrophy} style={{ color: '#C0C0C0', marginRight: '4px' }} />; // Junior Cup - silver
    }
    // League - use trophy with tier-based color
    return <FontAwesomeIcon icon={faTrophy} style={{ color: getLeagueColor(competition, gameState.config.LEAGUE_NAMES), marginRight: '4px' }} />;
};

export const TeamFixtures = ({ teamName, gameState, pushView, isAiPunditEnabled, showRatingsOnMatchReport, onCacheAiMatchReport }: Omit<TeamFixturesProps, 'leagueName'>) => {
    const [selectedFixture, setSelectedFixture] = useState<DisplayFixture | null>(null);

    const teamFixtures = useMemo(() => {
        return FixturesHelper.getAllFixturesForTeam(gameState.fixtures, teamName, gameState.cups);
    }, [teamName, gameState.fixtures, gameState.cups]);

    const getPlayerById = useCallback((id: number): Player | undefined => {
        for (const league of Object.values(gameState.leagues)) {
            for (const team of league.teams) {
                const player = team.squad.find(p => p.id === id);
                if (player) return player;
            }
        }
        return undefined;
    }, [gameState.leagues]);

    const getRoundNameForFixture = useCallback((fixture: DisplayFixture): string | undefined => {
        for (const cup of Object.values(gameState.cups)) {
            if (cup.name === fixture.competition) {
                const round = cup.rounds?.find(r => Math.floor(r.week) === Math.floor(fixture.week));
                return round?.name;
            }
        }
        return undefined;
    }, [gameState.cups]);

    const handleFixtureClick = (fixture: DisplayFixture) => {
        if (!fixture.result) return;
        soundService.playClick();
        setSelectedFixture(fixture);
    }
    
    const handleTeamClick = (e: React.MouseEvent, teamName: string) => {
        e.stopPropagation();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName });
    };

    const handleCompetitionClick = (e: React.MouseEvent, competition: string) => {
        e.stopPropagation();
        const cupNames = gameState.gameType === 'womens' ? WOMEN_CUP_NAMES : CUP_NAMES;
        const isCup = cupNames.includes(competition);

        pushView({
            type: 'competitions',
            title: 'Competitions',
            leagueName: competition,
            activeTab: isCup ? 'cup' : 'league'
        });
    };

    const getTeamByName = (name: string) => {
        for (const league of Object.values(gameState.leagues)) {
            const team = league.teams.find(t => t.name === name);
            if (team) return team;
        }
        return undefined;
    };

    return (
        <>
            <div className="panel panel-no-border-margin">
                 <div className="panel-header">Fixtures & Results</div>
                 <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr><th>W</th><th className="comp-column">Comp</th><th>Home</th><th>Away</th><th>Result</th></tr>
                        </thead>
                        <tbody>
                            {teamFixtures.length > 0 ? (
                                teamFixtures.map((item: any) => {
                                    // Handle placeholder rows (cup weeks or eliminated)
                                    if (item.type === 'placeholder') {
                                        const placeholderMessage = item.reason === 'eliminated_cup'
                                            ? '❌ Eliminated'
                                            : 'Awaiting draw';
                                        const compDisplay = `${item.competition}${item.cupRoundName ? ` - ${item.cupRoundName}` : ''}`;
                                        return (
                                            <tr key={`placeholder-${item.week}-${item.competition}-${item.cupRoundName || ''}`}>
                                                <td>{Math.floor(item.week)}</td>
                                                <td className="comp-column" title={compDisplay}><button className="link-style" onClick={(e) => handleCompetitionClick(e, item.competition)} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>{getCompetitionEmoji(item.competition, gameState)} {compDisplay}</button></td>
                                                <td colSpan={3} style={{textAlign: 'center', opacity: 0.7, fontStyle: 'italic'}}>
                                                    {placeholderMessage}
                                                </td>
                                            </tr>
                                        );
                                    }

                                    // Handle actual fixture rows
                                    const f = item as DisplayFixture;
                                    const roundName = getRoundNameForFixture(f);
                                    const compDisplay = roundName ? `${f.competition} - ${roundName}` : f.competition;
                                    return (
                                        <tr key={`${f.week}-${f.home}-${f.away}-${f.competition}`}
                                            className={`${f.result ? 'clickable-fixture' : ''} ${teamName === gameState.playerTeamName ? 'highlight' : ''}`}
                                            onClick={() => handleFixtureClick(f)}>
                                            <td>{Math.floor(f.week)}</td>
                                            <td className="comp-column" title={compDisplay}><button className="link-style" onClick={(e) => handleCompetitionClick(e, f.competition)} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>{getCompetitionEmoji(f.competition, gameState)} {compDisplay}</button></td>
                                            <td><button className="link-style" onClick={(e) => handleTeamClick(e, f.home)}><span className={f.home === gameState.playerTeamName ? 'player-team-name' : ''}>{getTeamByName(f.home) && <TeamBadge badge={getTeamByName(f.home)?.badge} color={getTeamByName(f.home)?.colors.primary} size="sm" />}{f.home}</span></button></td>
                                            <td><button className="link-style" onClick={(e) => handleTeamClick(e, f.away)}><span className={f.away === gameState.playerTeamName ? 'player-team-name' : ''}>{getTeamByName(f.away) && <TeamBadge badge={getTeamByName(f.away)?.badge} color={getTeamByName(f.away)?.colors.primary} size="sm" />}{f.away}</span></button></td>
                                            <td>
                                                {f.result ? (
                                                    <div>
                                                        <span className={`match-result ${getResultClass(f, teamName)}`}>
                                                            {f.result.homeScore} - {f.result.awayScore}
                                                        </span>
                                                        {f.result.penalties && (
                                                            <div style={{ fontSize: '0.75em', opacity: 0.7 }}>({f.result.penalties.homeScore}-{f.result.penalties.awayScore} p)</div>
                                                        )}
                                                    </div>
                                                ) : '-'}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={5} style={{textAlign: 'center', padding: '20px', opacity: 0.7}}>
                                        No fixtures scheduled
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {selectedFixture && (
                <MatchDetailsModal
                    isOpen={!!selectedFixture}
                    onClose={() => setSelectedFixture(null)}
                    fixture={selectedFixture}
                    getPlayerById={getPlayerById}
                    gameState={gameState}
                    pushView={pushView}
                    isAiPunditEnabled={isAiPunditEnabled}
                    showRatingsOnMatchReport={showRatingsOnMatchReport}
                    onCacheAiMatchReport={onCacheAiMatchReport}
                />
            )}
        </>
    );
};